"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useQuery } from "convex/react";
import { AnimatePresence, MotionConfig } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Trash2, ShoppingBag, XIcon } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ItemCustomizationSheet } from "@/components/order/item-customization-sheet";
import { CartItemRow } from "@/components/order/cart-item-row";
import { DeliveryAddressSheet } from "@/components/order/delivery-address-sheet";
import { useCartTotals, formatPrice } from "@/stores/cart-store";
import {
  browsePathForScope,
  useActiveCartStore,
  useCartScope,
} from "@/components/order/cart-scope";

import { useOrderingState } from "@/hooks/use-ordering-state";
import { telHref } from "@/lib/phone";
import { navLinkTransitionTypes } from "@/lib/nav-transition-types";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { getPublicPageBrand } from "@/lib/public-brand-theme";
import { getRomaFunnelLayoutAttribute } from "@/lib/roma-funnel-tokens";
import { api } from "../../../convex/_generated/api";
import type { Doc } from "../../../convex/_generated/dataModel";
import { isMenuItemWebOrderable } from "@convex/lib/menuServicePolicy";

interface CartSheetProps {
  children: React.ReactNode;
}

const ORDERING_TIME_ZONE = "America/Los_Angeles";

/**
 * Short label for the next pickup window, in restaurant-local time. Mirrors
 * the checkout page's pickup time label: "today at 11:15 AM",
 * "tomorrow at 11:15 AM", or "Tue at 11:15 AM" when further out.
 */
export function nextPickupLabel(timestamp: number): string {
  const targetDate = new Date(timestamp);
  const dayKey = (date: Date) =>
    date.toLocaleDateString("en-CA", { timeZone: ORDERING_TIME_ZONE });
  const time = targetDate.toLocaleTimeString("en-US", {
    timeZone: ORDERING_TIME_ZONE,
    hour: "numeric",
    minute: "2-digit",
  });

  const now = new Date();
  const targetDay = dayKey(targetDate);
  if (targetDay === dayKey(now)) return `today at ${time}`;
  if (targetDay === dayKey(new Date(now.getTime() + 24 * 60 * 60 * 1000))) {
    return `tomorrow at ${time}`;
  }

  const weekday = targetDate.toLocaleDateString("en-US", {
    timeZone: ORDERING_TIME_ZONE,
    weekday: "short",
  });
  return `${weekday} at ${time}`;
}

/**
 * Compact chip copy for the sticky menu pickup-time pill. Prefer this over
 * `Pickup ${nextPickupLabel(...)}` so mobile widths stay readable:
 * "Opens 4:15 PM", "Tomorrow 4:15 PM", or "Tue 4:15 PM".
 */
export function nextPickupChipLabel(timestamp: number): string {
  const targetDate = new Date(timestamp);
  const dayKey = (date: Date) =>
    date.toLocaleDateString("en-CA", { timeZone: ORDERING_TIME_ZONE });
  const time = targetDate.toLocaleTimeString("en-US", {
    timeZone: ORDERING_TIME_ZONE,
    hour: "numeric",
    minute: "2-digit",
  });

  const now = new Date();
  const targetDay = dayKey(targetDate);
  if (targetDay === dayKey(now)) return `Opens ${time}`;
  if (targetDay === dayKey(new Date(now.getTime() + 24 * 60 * 60 * 1000))) {
    return `Tomorrow ${time}`;
  }

  const weekday = targetDate.toLocaleDateString("en-US", {
    timeZone: ORDERING_TIME_ZONE,
    weekday: "short",
  });
  return `${weekday} ${time}`;
}

/**
 * Live pickup availability for the current public location. `available` is
 * false outside open hours and within 30 minutes of close; the server rejects
 * order placement in the same window, so this gate is UX, not enforcement.
 * Pass `enabled: false` (browse-only mode, sheet closed) to skip the query
 * entirely.
 */
export function useOrderingPickupAvailability(enabled: boolean): {
  orderingClosed: boolean;
  nextOpenAt: number | undefined;
} {
  const { location } = useMarketingLocationContext();
  // Minute-resolution clock so the gate flips live at the open/close cutoffs
  // without a reload (Convex queries don't re-run on wall-clock time).
  const [nowMinute, setNowMinute] = useState(
    () => Math.floor(Date.now() / 60000) * 60000,
  );
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(
      () => setNowMinute(Math.floor(Date.now() / 60000) * 60000),
      30 * 1000,
    );
    return () => clearInterval(id);
  }, [enabled]);

  const availability = useQuery(
    api.orderSettings.isAvailable,
    enabled
      ? { orderType: "pickup" as const, locationId: location.id, now: nowMinute }
      : "skip",
  );
  const orderingClosed =
    enabled && availability !== undefined && !availability.available;

  return {
    orderingClosed,
    nextOpenAt: orderingClosed ? availability?.nextOpenAt : undefined,
  };
}

export function CartSheet({ children }: CartSheetProps) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [deliveryAddressOpen, setDeliveryAddressOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  // Gate row enter-animations until after the open list has painted, so the
  // initial cart doesn't stagger when the sheet opens — only rows inserted
  // afterward (e.g. upsells added from the edit sheet) animate in.
  const [animateEnter, setAnimateEnter] = useState(false);
  // The Edit button that opened the item sheet, so focus returns to it on close
  // (the sheet's trigger is external, so Radix can't restore focus itself).
  const lastEditTriggerRef = useRef<HTMLButtonElement | null>(null);
  const cartScope = useCartScope();
  const useCart = useActiveCartStore();
  const isCateringCart = cartScope === "catering";
  const browseHref = browsePathForScope(cartScope);
  const items = useCart((state) => state.items);
  const orderType = useCart((state) => state.orderType);
  const deliveryAddress = useCart((state) => state.deliveryAddress);
  const cateringScheduledFor = useCart((state) => state.scheduledFor);
  const clearCart = useCart((state) => state.clearCart);
  const { subtotal, tax, deliveryFee, tip, discount, total, itemCount } =
    useCartTotals(useCart);
  const { location, siteConfig } = useMarketingLocationContext();
  const pageBrand = getPublicPageBrand(siteConfig);
  const {
    checkoutEnabled: mealCheckoutEnabled,
    deliveryOrderingEnabled: mealDeliveryOrderingEnabled,
    fallbackPhone,
  } = useOrderingState();
  const cateringLocationId = useCart((state) => state.cateringLocationId);
  const cateringLocationChosen = useCart((state) => state.cateringLocationChosen);
  // Catering carts gate on the tenant's catering configuration and on a
  // chosen fulfillment + scheduled time (set on the catering page), never on
  // the meal-ordering hours or the Uber delivery switch.
  const cateringConfig = useQuery(
    api.cateringSettings.getPublic,
    isCateringCart ? { locationId: location.id } : "skip",
  );
  // A pickup location that is visible for browsing but not orderable yet
  // cannot proceed to checkout (the server refuses it as well).
  const cateringPickupOrderable =
    orderType !== "pickup" ||
    cateringConfig?.pickupLocations.some(
      (entry) => entry.locationId === cateringLocationId && entry.orderable,
    ) === true;
  // Pickup requires an explicit location choice (never a stored auto-default
  // or the kitchen assigned by a delivery switch).
  const cateringPickupChosen = orderType !== "pickup" || cateringLocationChosen;
  const cateringSelectionReady =
    Boolean(cateringConfig?.enabled) &&
    cateringScheduledFor != null &&
    Boolean(cateringLocationId) &&
    cateringPickupChosen &&
    cateringPickupOrderable &&
    (orderType === "pickup" ||
      Boolean(cateringConfig?.delivery.enabled && deliveryAddress));
  const checkoutEnabled = isCateringCart
    ? cateringSelectionReady
    : mealCheckoutEnabled;
  const deliveryOrderingEnabled = isCateringCart
    ? Boolean(cateringConfig?.delivery.enabled)
    : mealDeliveryOrderingEnabled;
  const menuItems = useQuery(
    api.menu.getAll,
    open ? { locationId: location.id } : "skip"
  ) as Doc<"menu_items">[] | undefined;

  const isEmpty = items.length === 0;
  const editingItem = items.find((item) => item.id === editingItemId) ?? null;
  const editingMenuItem =
    menuItems?.find((menuItem) => menuItem._id === editingItem?.menuItemId) ?? null;
  const nonOrderableCartItems = useMemo(() => {
    const menuItemsById = new Map(
      menuItems?.map((menuItem) => [menuItem._id, menuItem]) ?? [],
    );

    return items.filter((item) => {
      const menuItem = menuItemsById.get(item.menuItemId);
      return menuItem !== undefined && !isMenuItemWebOrderable(menuItem);
    });
  }, [items, menuItems]);
  const hasNonOrderableCartItems = nonOrderableCartItems.length > 0;
  // Set lookup so a long cart doesn't do an O(n*m) scan per row.
  const editableIds = useMemo(
    () =>
      new Set(
        menuItems
          ?.filter((menuItem) => isMenuItemWebOrderable(menuItem))
          .map((menuItem) => menuItem._id) ?? [],
      ),
    [menuItems],
  );

  // Drop the editing target if the item vanishes from the cart (removed/merged).
  // Close-path resets live in onOpenChange below.
  useEffect(() => {
    if (editingItemId && !editingItem) {
      setEditingItemId(null);
    }
  }, [editingItem, editingItemId]);

  // Enable row enter-animations one frame after the sheet opens (post-paint),
  // and disable on close so the next open starts un-staggered.
  useEffect(() => {
    if (!open) {
      setAnimateEnter(false);
      return;
    }
    const raf = requestAnimationFrame(() => setAnimateEnter(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const missingDeliveryAddress =
    deliveryOrderingEnabled &&
    orderType === "delivery" &&
    !deliveryAddress;

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setEditingItemId(null);
          }
        }}
      >
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent
        showCloseButton={false}
        data-mobile-cart-sheet
        data-order-light-surface="true"
        // Portaled to document.body (outside the public layout wrapper), so
        // re-apply the brand palette. Roma funnel remaps terracotta to brand red.
        data-public-page-brand={pageBrand}
        data-roma-funnel={getRomaFunnelLayoutAttribute(pageBrand)}
        className="flex h-full w-full max-w-full flex-col gap-0 border-l border-border/40 bg-background p-0 text-foreground shadow-2xl data-[state=open]:ease-out data-[state=open]:duration-300 data-[state=closed]:ease-in data-[state=closed]:duration-200 sm:max-w-md"
      >
        <SheetHeader className="sticky top-0 z-10 shrink-0 border-b border-border/40 bg-background px-4 py-3 shadow-[0_10px_24px_-22px_rgb(0_0_0_/_0.42)] sm:px-6 sm:py-4">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 text-base font-bold tracking-tight sm:gap-2.5 sm:text-lg">
              <ShoppingBag className="h-5 w-5 text-[var(--roma-terracotta)]" />
              Your order
            </SheetTitle>
            <SheetDescription className="sr-only">
              Review your {orderType} order, adjust quantities, and proceed to
              checkout.
            </SheetDescription>
            <div className="flex items-center gap-2">
              {itemCount > 0 && (
                <>
                  <button
                    type="button"
                    className="flex h-11 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium text-muted-foreground transition-all hover:text-destructive focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:scale-95 sm:h-10"
                    onClick={() => {
                      // Snapshot before clearing so a mis-tap on the persisted
                      // cart is recoverable. Restoring items by id preserves the
                      // exact rows without re-firing add analytics.
                      const snapshot = useCart.getState().items;
                      clearCart();
                      setEditingItemId(null);
                      toast("Order cleared", {
                        action: {
                          label: "Undo",
                          onClick: () =>
                            useCart.setState({ items: snapshot }),
                        },
                      });
                    }}
                    aria-label="Clear order"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Clear order
                  </button>
                  <span className="rounded-full bg-muted/50 px-2.5 py-1 text-[11px] font-black uppercase tracking-widest text-muted-foreground sm:px-3 sm:text-xs">
                    {itemCount} {itemCount === 1 ? "item" : "items"}
                  </span>
                </>
              )}
              <SheetClose asChild>
                <button
                  type="button"
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-muted/60 text-muted-foreground transition-all hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:scale-95 sm:h-10 sm:w-10"
                  aria-label="Close order"
                >
                  <XIcon className="h-4.5 w-4.5" />
                </button>
              </SheetClose>
            </div>
          </div>
        </SheetHeader>

        {isEmpty ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-5 p-8 pb-[var(--sticky-bar-pb)] text-center">
            <div className="rounded-full bg-muted/30 p-6 ring-1 ring-border/20">
              <ShoppingBag className="h-12 w-12 text-muted-foreground/40" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-black tracking-tight">Your order is empty</h3>
              <p className="text-sm text-muted-foreground max-w-[260px] mx-auto leading-relaxed">
                Add a few favorites from the menu and they&rsquo;ll be waiting here.
              </p>
            </div>
            <SheetClose asChild>
              <Button className="rounded-full px-8 h-12 font-bold shadow-lg shadow-primary/10 transition-all" asChild>
                <Link href={browseHref}>Browse the menu</Link>
              </Button>
            </SheetClose>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <ScrollArea
              className="flex-1 min-h-0"
              data-mobile-side-cart-items
            >
              <MotionConfig reducedMotion="user">
                <div className="space-y-2 px-4 py-3 pb-2 sm:space-y-2.5 sm:px-6 sm:py-4">
                  <AnimatePresence mode="popLayout">
                    {items.map((item) => (
                      <CartItemRow
                        key={item.id}
                        item={item}
                        canEdit={Boolean(menuItems && editableIds.has(item.menuItemId))}
                        animateEnter={animateEnter}
                        onEdit={(trigger) => {
                          if (!menuItems || !editableIds.has(item.menuItemId)) return;
                          lastEditTriggerRef.current = trigger;
                          setEditingItemId(item.id);
                        }}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </MotionConfig>
            </ScrollArea>

            {/* Order Summary & Footer */}
            <div
              className="shrink-0 mt-auto border-t border-border/40 bg-background shadow-[0_-10px_24px_-22px_rgb(0_0_0_/_0.42)]"
              data-mobile-side-cart-footer
            >
              <div className="space-y-2 px-4 pb-2 pt-3 sm:space-y-2.5 sm:px-6 sm:pb-3 sm:pt-4">
                <div className="space-y-1.5 text-[13px] font-medium sm:space-y-2">
                  <div className="flex justify-between items-center text-muted-foreground">
                    <span>Subtotal</span>
                    <span className="font-semibold tabular-nums text-foreground">{formatPrice(subtotal)}</span>
                  </div>

                  {discount > 0 && (
                    <div className="flex justify-between items-center font-semibold text-[var(--roma-terracotta)]">
                      <span>Discount</span>
                      <span className="tabular-nums">−{formatPrice(discount)}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center text-muted-foreground">
                    <span>Tax</span>
                    <span className="font-semibold tabular-nums text-foreground">{formatPrice(tax)}</span>
                  </div>

                  {deliveryOrderingEnabled &&
                    orderType === "delivery" &&
                    deliveryFee > 0 && (
                      <div className="flex justify-between items-center text-muted-foreground">
                        <span>{isCateringCart ? "Delivery fee" : "Uber courier fee"}</span>
                        <span className="font-semibold tabular-nums text-foreground">{formatPrice(deliveryFee)}</span>
                      </div>
                    )}

                  {tip > 0 && (
                    <div className="flex justify-between items-center text-muted-foreground">
                      <span>Tip · adjust at checkout</span>
                      <span className="font-semibold tabular-nums text-foreground">{formatPrice(tip)}</span>
                    </div>
                  )}
                </div>

                {isCateringCart && !cateringSelectionReady && (
                  <Link
                    href={browseHref}
                    onClick={() => setOpen(false)}
                    data-catering-selection-prompt
                    className="flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2 text-left text-xs font-bold text-foreground transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                      {cateringConfig && !cateringConfig.enabled
                        ? "Catering ordering is not available"
                        : !cateringPickupChosen
                          ? "Choose a pickup location"
                          : !cateringPickupOrderable
                            ? "This pickup location is not taking online orders yet"
                            : "Choose pickup or delivery and a time"}
                    </span>
                    <span className="text-[11px] font-medium text-muted-foreground">
                      Required
                    </span>
                  </Link>
                )}

                {!isCateringCart && missingDeliveryAddress && (
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      setDeliveryAddressOpen(true);
                    }}
                    className="flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2 text-left text-xs font-bold text-foreground transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="flex items-center gap-2">
                      <MapPin
                        className="h-4 w-4 shrink-0 text-primary"
                        aria-hidden="true"
                      />
                      Add delivery address
                    </span>
                    <span className="text-[11px] font-medium text-muted-foreground">
                      Required
                    </span>
                  </button>
                )}

                {hasNonOrderableCartItems && (
                  <div
                    data-cart-service-policy-notice
                    className="rounded-xl border border-amber-500/30 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950"
                  >
                    <p className="font-bold">Dine-in only</p>
                    <p>
                      {nonOrderableCartItems
                        .map((item) => item.name)
                        .join(", ")} {" "}
                      cannot be ordered online. Remove {" "}
                      {nonOrderableCartItems.length === 1 ? "it" : "them"} to
                      continue.
                    </p>
                  </div>
                )}

                <Separator className="bg-border/20 my-0.5" />

                <div className="flex justify-between items-center">
                  <span className="text-lg font-black tracking-tight sm:text-xl">Total</span>
                  <span className="text-xl font-black tabular-nums tracking-tighter sm:text-2xl">
                    {formatPrice(total)}
                  </span>
                </div>

                <div role="status" aria-live="polite" className="sr-only">
                  {itemCount} {itemCount === 1 ? "item" : "items"}, total {formatPrice(total)}
                </div>
              </div>

              {/* Checkout Button Section */}
              <div className="px-4 pb-[var(--sticky-bar-pb)] pt-1 sm:px-6 sm:pt-2">
                {hasNonOrderableCartItems ? (
                  <Button
                    className="h-14 w-full rounded-2xl text-base font-black sm:text-lg"
                    size="lg"
                    disabled
                  >
                    Remove dine-in-only item to continue
                  </Button>
                ) : checkoutEnabled ? (
                  <Button
                    className="h-14 w-full rounded-2xl bg-[var(--roma-terracotta)] text-base font-black text-white shadow-xl shadow-[0_14px_42px_-22px_color-mix(in_srgb,var(--roma-terracotta)_85%,transparent)] transition-all hover:bg-[var(--roma-saffron-deep)] active:scale-[0.98] sm:text-lg"
                    size="lg"
                    asChild
                  >
                    {isCateringCart ? (
                      <Link
                        href="/order/catering/checkout"
                        onClick={() => setOpen(false)}
                        transitionTypes={navLinkTransitionTypes(
                          "/order/catering/checkout",
                          pathname,
                        )}
                      >
                        <span className="tracking-tight">Continue to checkout</span>
                      </Link>
                    ) : (
                      <Link
                        href="/order/checkout"
                        onClick={() => setOpen(false)}
                        transitionTypes={navLinkTransitionTypes(
                          "/order/checkout",
                          pathname,
                        )}
                      >
                        <span className="tracking-tight">Continue to checkout</span>
                      </Link>
                    )}
                  </Button>
                ) : (
                  <div
                    data-cart-phone-order-fallback
                    className="space-y-2 rounded-2xl border border-[#eadbd3] bg-[#fffaf7] p-3"
                  >
                    <p className="text-center text-[12px] font-semibold leading-5 text-[#5b4a43]">
                      {isCateringCart
                        ? `Finish your catering details above, or call ${siteConfig.name} to place this order by phone.`
                        : `Online checkout is paused. Call ${siteConfig.name} to place this pickup order by phone.`}
                    </p>
                    <a
                      href={telHref(fallbackPhone)}
                      className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#0d0d0d] text-base font-black text-white transition-colors hover:bg-[#262626] sm:text-lg"
                      aria-label="Call to order pickup by phone"
                    >
                      <Phone className="h-4 w-4" aria-hidden="true" />
                      <span className="tracking-tight">
                        Call {fallbackPhone}
                      </span>
                    </a>
                  </div>
                )}
                {checkoutEnabled && (!deliveryOrderingEnabled || orderType !== "delivery") && (
                  <p
                    data-pickup-payment-hint
                    className="mt-2 text-center text-sm font-medium text-zinc-600"
                  >
                    You’ll pay securely online at checkout.
                  </p>
                )}
                {!checkoutEnabled && !hasNonOrderableCartItems && (
                  <p
                    data-checkout-paused-hint
                    className="mt-2 text-center text-[11px] font-medium text-muted-foreground/70"
                  >
                    Have your cart ready when you call.
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </SheetContent>
      {editingItem && editingMenuItem && (
        <ItemCustomizationSheet
          item={editingMenuItem}
          open={Boolean(editingItemId)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setEditingItemId(null);
              const trigger = lastEditTriggerRef.current;
              requestAnimationFrame(() => trigger?.focus());
            }
          }}
          editingItem={editingItem}
        />
      )}
      </Sheet>
      <DeliveryAddressSheet
        open={deliveryAddressOpen}
        onOpenChange={setDeliveryAddressOpen}
      />
    </>
  );
}
